import { Spacing } from '@persona-id/design-tokens';
import { TrashIcon } from '@persona-id/design-tokens/icons';
import { ReactNode, useCallback, useMemo, useState } from 'react';
import {
  DragDropContext,
  Droppable,
  DroppableProvided,
  DroppableStateSnapshot,
  DropResult,
} from 'react-beautiful-dnd';
import { COLORS } from 'shared/styles/variables';
import styled, { css } from 'styled-components';

import {
  Asset,
  WorkflowVersionMap,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import { ComponentValidationError } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/component-validations/types';
import ComponentConfigSetting from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentConfigSetting/ComponentConfigSetting';
import HoverableDraggable from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentConfigSetting/ConfigSetting/HoverableDraggable';
import { TranslationsConfig } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/TranslationsEditor';
import {
  ComponentSettingConfig,
  ComponentSettingImage,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/types';
import { SCREEN_EDITOR_CONTROL_PANEL_ID } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenEditor/InquiryTemplateScreenEditor';
import TranslationEditSection from 'dashboard/apps/InquiryTemplateEditor/TranslationEditSection';
import TranslationsPanel from 'dashboard/apps/InquiryTemplateEditor/TranslationsPanel';
import { uiStepComponentsRecursive } from 'dashboard/apps/InquiryTemplateEditor/utils/steps';
import Divider from 'dashboard/components/Divider';
import Markdown from 'dashboard/components/Markdown';
import HelpIcon from 'dashboard/components-lib/HelpIcon';
import { reorder } from 'dashboard/lib/dnd/utils';
import {
  InquiryTemplateVersionStep,
  InquiryTemplateVersionStepUi,
} from 'dashboard/models/inquiry-template-version';
import Button from 'design-system/components/Button';
import { Label, Paragraph } from 'design-system/components/Text';
import { InquiryIso6391LanguageCode } from 'lib/constants/locales';
import { FieldSchema } from 'lib/fields';
import useToggle from 'lib/hooks/useToggle';
import {
  StepperComponentConfig,
  StepperStepConfig,
  UiComponent,
  UiComponentConfigType,
} from 'lib/interfaces/ui-components';

const ConfigList = styled.div<{ isDraggingOver: boolean }>`
  ${({ isDraggingOver }) => css`
    background-color: ${isDraggingOver ? COLORS.indigo10 : COLORS.white};
  `};
  transition:
    background-color 0.4s ease-in-out,
    box-shadow 0.4s ease-in-out;
`;

const ItemContainer = styled.section`
  flex: 1;
  display: flex;
  gap: ${Spacing[2]};
  flex-direction: column;
`;

const HeadingContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

interface ConfigSettingProps {
  editable: boolean;
  componentSetting: ComponentSettingConfig;
  componentConfig: StepperComponentConfig['config'];
  componentType: UiComponentConfigType;
  componentPreview: UiComponent;
  currentLocale: InquiryIso6391LanguageCode;
  enabledLocales: InquiryIso6391LanguageCode[];
  stepName: string;
  steps: InquiryTemplateVersionStep[];
  fieldSchema: FieldSchema[];
  workflowVersionMap: WorkflowVersionMap;
  componentValidationErrors: ComponentValidationError[] | undefined;
  isNewComponent: boolean;
  basePath: string;
  currentStep: InquiryTemplateVersionStepUi;
  getStepLocalizedStrings: (locales?: Array<InquiryIso6391LanguageCode>) => Promise<void>;
  handleUpdateComponentConfig: (componentConfigUpdates: Record<string, any>) => void;
  handleImageSelect: (asset: Asset, imageSetting: ComponentSettingImage) => void;
}

const ConfigSetting = ({
  editable,
  basePath,
  componentSetting,
  componentConfig,
  componentPreview,
  componentType,
  currentLocale,
  enabledLocales,
  stepName,
  workflowVersionMap,
  steps,
  fieldSchema,
  isNewComponent,
  componentValidationErrors,
  currentStep,
  getStepLocalizedStrings,
  handleImageSelect,
  handleUpdateComponentConfig,
}: ConfigSettingProps): JSX.Element => {
  const [translationsConfig, setTranslationsConfig] = useState<TranslationsConfig | undefined>(
    undefined,
  );

  const {
    'client-key': clientKey,
    'helper-text': helperText,
    'config-label': configLabel,
    'empty-config': emptyConfig,
    label,
    settings,
    translations,
  } = componentSetting;

  const configList = componentConfig?.[
    clientKey as keyof StepperComponentConfig['config']
  ] as StepperStepConfig[];

  const [
    showTranslationsPanel,
    { enable: openTranslationsPanel, disable: closeTranslationsPanel },
  ] = useToggle(false);

  const handleDragEnd = (result: DropResult) => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }

    const reorderedConfigList = reorder(configList, result.source.index, result.destination.index);

    handleUpdateComponentConfig({
      [clientKey]: reorderedConfigList,
    });
  };

  const handleDeleteConfig = (indexToRemove: number) => {
    const filteredList = configList.filter((_, idx) => idx !== indexToRemove);

    handleUpdateComponentConfig({
      [clientKey]: filteredList,
    });
  };

  const handleOpenTranslationsPanel = useCallback(
    (config: TranslationsConfig) => {
      setTranslationsConfig(config);
      openTranslationsPanel();
    },
    [openTranslationsPanel, setTranslationsConfig],
  );

  const configSettings: ReactNode[] = useMemo(
    () =>
      configList?.map((nestedConfig, configIndex) => (
        <HoverableDraggable
          key={configIndex}
          index={configIndex}
          draggableId={configIndex.toString()}
          isDragDisabled={!editable}
        >
          <ItemContainer>
            <HeadingContainer>
              <Label weight='bold'>{`${configLabel} ${configIndex + 1}`}</Label>
              <Button
                variant='danger'
                width='icon'
                type='button'
                onClick={() => handleDeleteConfig(configIndex)}
                id='FLE-config-setting__delete-config'
              >
                <TrashIcon />
              </Button>
            </HeadingContainer>
            {translations.map((schema, translationIndex) => {
              const {
                label,
                'client-key': translationClientKey,
                'custom-translations-key': translationsKey,
                'component-preview-name': componentPreviewName,
              } = schema;

              const translationComponentPreview = uiStepComponentsRecursive([
                componentPreview,
              ]).find(
                (component) =>
                  component.name === componentPreviewName?.replace('index', configIndex.toString()),
              );

              if (translationComponentPreview) {
                const sectionConfig = {
                  path: `${basePath}.${clientKey}.${configIndex}.${translationsKey}`,
                  translationsKey: `${clientKey}.${configIndex}.${translationsKey}`,
                  stepName: stepName,
                  name: translationComponentPreview.name,
                  clientKey: clientKey,
                  defaultValue:
                    translationComponentPreview.attributes[
                      translationClientKey as keyof UiComponent['attributes']
                    ],
                };

                return (
                  <>
                    <TranslationEditSection
                      key={translationIndex}
                      config={sectionConfig}
                      label={label}
                      enabledLocales={enabledLocales}
                      currentLocale={currentLocale}
                      editable={editable}
                      handleOpenPanel={(config: TranslationsConfig) =>
                        handleOpenTranslationsPanel(config)
                      }
                      getStepLocalizedStrings={getStepLocalizedStrings}
                      handleTranslationUpdates={handleUpdateComponentConfig}
                    />
                  </>
                );
              }
            })}
            {settings.map((setting, idx) => (
              <ComponentConfigSetting
                data-rbd-drag-handle-draggable-id='gibberish'
                key={idx}
                basePath={basePath}
                componentSetting={setting}
                editable={editable}
                componentConfig={nestedConfig}
                componentType={componentType}
                componentPreview={componentPreview}
                stepName={stepName}
                enabledLocales={enabledLocales}
                currentLocale={currentLocale}
                getStepLocalizedStrings={getStepLocalizedStrings}
                handleUpdateComponentConfig={handleUpdateComponentConfig}
                updatePathPrefix={`${clientKey}.${configIndex}`}
                handleImageSelect={handleImageSelect}
                workflowVersionMap={workflowVersionMap}
                steps={steps}
                fieldSchema={fieldSchema}
                isNewComponent={isNewComponent}
                componentValidationErrors={componentValidationErrors}
                currentStep={currentStep}
              />
            ))}
            <Divider />
          </ItemContainer>
        </HoverableDraggable>
      )),
    [
      basePath,
      clientKey,
      componentPreview,
      componentType,
      componentValidationErrors,
      configLabel,
      configList,
      currentLocale,
      currentStep,
      editable,
      enabledLocales,
      fieldSchema,
      getStepLocalizedStrings,
      handleImageSelect,
      handleOpenTranslationsPanel,
      handleUpdateComponentConfig,
      isNewComponent,
      settings,
      stepName,
      steps,
      translations,
      workflowVersionMap,
    ],
  );

  const renderConfigSections = useCallback(
    (droppableProvided: DroppableProvided, droppableSnapshot: DroppableStateSnapshot) => (
      <ConfigList
        ref={droppableProvided.innerRef}
        isDraggingOver={droppableSnapshot.isDraggingOver}
      >
        {configSettings}
        {droppableProvided.placeholder}
      </ConfigList>
    ),
    [configSettings],
  );

  const handleAddNewConfig = useCallback(() => {
    handleUpdateComponentConfig({
      [clientKey]: [...configList, emptyConfig],
    });
  }, [handleUpdateComponentConfig, clientKey, configList, emptyConfig]);

  return (
    <>
      <Label weight='bold'>
        {label}
        {helperText && (
          <HelpIcon>
            <Paragraph size='sm'>
              <Markdown>{helperText}</Markdown>
            </Paragraph>
          </HelpIcon>
        )}
      </Label>

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId='droppable'>{renderConfigSections}</Droppable>
      </DragDropContext>

      <Button
        type='button'
        variant='basic'
        width='auto'
        id='fle__add-new-config-setting'
        onClick={handleAddNewConfig}
      >
        Add New {configLabel}
      </Button>
      {showTranslationsPanel && translationsConfig && (
        <TranslationsPanel
          offsetElementId={SCREEN_EDITOR_CONTROL_PANEL_ID}
          config={translationsConfig}
          currentLocale={currentLocale}
          enabledLocales={enabledLocales}
          editable={editable}
          getStepLocalizedStrings={getStepLocalizedStrings}
          handleTranslationUpdates={handleUpdateComponentConfig}
          onOutsideClick={closeTranslationsPanel}
        />
      )}
    </>
  );
};

export default ConfigSetting;
